import { useState, useEffect } from 'react';
import { FaVolumeUp, FaSpinner } from 'react-icons/fa';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { generateBatchSpeechAudio, LanguageCodes, VoiceGenders } from '../../../utilities/extensions/textToSpeech';
import { db } from '../../../firebase';
import { generateActivityStoragePath } from '../../../utilities/storagePathHelper';

const TensesActivity = ({ initialContent, onContentChange, chapterId, grade, selectedChapter }) => {
  const [content, setContent] = useState({
    groups: [
      {
        image: null,
        sentences: [
          { languageText: '', englishText: '', tenseEnglish: '', tenseKannada: '', languageAudio: null, englishAudio: null },
          { languageText: '', englishText: '', tenseEnglish: '', tenseKannada: '', languageAudio: null, englishAudio: null },
          { languageText: '', englishText: '', tenseEnglish: '', tenseKannada: '', languageAudio: null, englishAudio: null }
        ]
      }
    ]
  });

  const [uploadingFiles, setUploadingFiles] = useState({});
  const [generatingAudio, setGeneratingAudio] = useState(false);

  useEffect(() => {
    if (initialContent) {
      try {
        const parsed = JSON.parse(initialContent);
        if (parsed && Array.isArray(parsed.groups)) {
          setContent(parsed);
        }
      } catch {
        // keep default
      }
    }
  }, [initialContent]);

  useEffect(() => {
    onContentChange(content);
  }, [content, onContentChange]);

  const buildStoragePath = ({ groupIndex, fileName }) => {
    const language = selectedChapter?.subject?.toLowerCase() || 'language';
    const gradeStr = `grade${grade}`;

    return generateActivityStoragePath({
      language,
      grade: gradeStr,
      chapterId,
      activityName: 'tenses',
      fileName,
      itemId: `group${groupIndex + 1}`
    });
  };

  const TENSE_OPTIONS = [
    { value: 'Past', label: 'Past Tense', kannada: 'ಭೂತಕಾಲ' },
    { value: 'Present', label: 'Present Tense', kannada: 'ವರ್ತಮಾನ ಕಾಲ' },
    { value: 'Future', label: 'Future Tense', kannada: 'ಭವಿಷ್ಯತ್ಕಾಲ' }
  ];

  const playAudio = async (audioPath) => {
    if (!audioPath) return;

    try {
      const storage = getStorage();
      const audioRef = ref(storage, audioPath);
      const url = await getDownloadURL(audioRef);
      const audio = new Audio(url);
      audio.play().catch(() => {});
    } catch (error) {
      alert(`Failed to play audio. Error: ${error.message}`);
    }
  };

  const updateSentence = (groupIndex, sentenceIndex, field, value) => {
    const groups = [...(content.groups || [])];
    const group = groups[groupIndex] || { sentences: [] };
    const sentences = [...(group.sentences || [])];
    sentences[sentenceIndex] = { ...(sentences[sentenceIndex] || {}), [field]: value };
    groups[groupIndex] = { ...group, sentences };
    setContent(prev => ({ ...prev, groups }));
  };

  const addGroup = () => {
    setContent(prev => ({
      ...prev,
      groups: [
        ...(prev.groups || []),
        {
          image: null,
          sentences: [
            { languageText: '', englishText: '', tenseEnglish: '', tenseKannada: '', languageAudio: null, englishAudio: null },
            { languageText: '', englishText: '', tenseEnglish: '', tenseKannada: '', languageAudio: null, englishAudio: null },
            { languageText: '', englishText: '', tenseEnglish: '', tenseKannada: '', languageAudio: null, englishAudio: null }
          ]
        }
      ]
    }));
  };

  const removeGroup = async (groupIndex) => {
    const groups = content.groups || [];
    if (groups.length <= 1) return;

    const group = groups[groupIndex];
    const storage = getStorage();

    if (group.image) {
      try {
        const imgRef = ref(storage, group.image);
        await deleteObject(imgRef);
      } catch {
        // ignore delete errors
      }
    }

    const updated = groups.filter((_, i) => i !== groupIndex);
    setContent(prev => ({ ...prev, groups: updated }));
  };

  const handleFileUpload = async (groupIndex, file) => {
    if (!file || !chapterId || !grade) return;

    const uploadKey = `${groupIndex}-image`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const storage = getStorage();
      const groups = content.groups || [];
      const existingPath = groups[groupIndex]?.image;

      if (existingPath) {
        try {
          const existingRef = ref(storage, existingPath);
          await deleteObject(existingRef);
        } catch {
          // ignore delete
        }
      }

      const storagePath = buildStoragePath({ groupIndex, fileName: file.name });
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);

      const updated = [...groups];
      updated[groupIndex] = { ...updated[groupIndex], image: storagePath };
      setContent(prev => ({ ...prev, groups: updated }));
    } catch {
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const previewImage = async (path) => {
    if (!path) return;
    try {
      const storage = getStorage();
      const imgRef = ref(storage, path);
      const url = await getDownloadURL(imgRef);
      window.open(url, '_blank');
    } catch (error) {
      alert(`Failed to preview image. Error: ${error.message}`);
    }
  };

  const buildAudioStoragePath = ({ groupIndex, sentenceIndex, field, fileName }) => {
    const language = selectedChapter?.subject?.toLowerCase() || 'language';
    const gradeStr = `grade${grade}`;

    return generateActivityStoragePath({
      language,
      grade: gradeStr,
      chapterId,
      activityName: 'tenses-audio',
      fileName,
      itemId: `group${groupIndex + 1}-sentence${sentenceIndex + 1}-${field}`
    });
  };

  const handleAudioUpload = async (groupIndex, sentenceIndex, field, file) => {
    if (!file || !chapterId || !grade) return;

    const uploadKey = `${groupIndex}-${sentenceIndex}-${field}`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const storage = getStorage();
      const groups = content.groups || [];
      const sentence = groups[groupIndex]?.sentences?.[sentenceIndex];
      const existingPath = sentence?.[field];

      if (existingPath) {
        try {
          const existingRef = ref(storage, existingPath);
          await deleteObject(existingRef);
        } catch {
          // ignore delete
        }
      }

      const storagePath = buildAudioStoragePath({ groupIndex, sentenceIndex, field, fileName: file.name });
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);

      setContent(prev => {
        const groupsUpd = [...(prev.groups || [])];
        const grp = groupsUpd[groupIndex] || { sentences: [] };
        const sentences = [...(grp.sentences || [])];
        sentences[sentenceIndex] = { ...(sentences[sentenceIndex] || {}), [field]: storagePath };
        groupsUpd[groupIndex] = { ...grp, sentences };
        return { ...prev, groups: groupsUpd };
      });
    } catch {
      alert('Failed to upload audio. Please try again.');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const handleAudioDelete = async (groupIndex, sentenceIndex, field) => {
    const groups = content.groups || [];
    const sentence = groups[groupIndex]?.sentences?.[sentenceIndex];
    const existingPath = sentence?.[field];
    if (!existingPath) return;

    try {
      const storage = getStorage();
      const audioRef = ref(storage, existingPath);
      await deleteObject(audioRef);
    } catch {
      // ignore delete errors
    }

    setContent(prev => {
      const groupsUpd = [...(prev.groups || [])];
      const grp = groupsUpd[groupIndex] || { sentences: [] };
      const sentences = [...(grp.sentences || [])];
      sentences[sentenceIndex] = { ...(sentences[sentenceIndex] || {}), [field]: null };
      groupsUpd[groupIndex] = { ...grp, sentences };
      return { ...prev, groups: groupsUpd };
    });
  };

  const generateAllAudio = async () => {
    if (!content.groups || content.groups.length === 0) {
      alert('No groups available to generate audio');
      return;
    }

    setGeneratingAudio(true);

    try {
      const storage = getStorage();

      const languageTexts = [];
      const englishTexts = [];
      const sentenceRefs = [];

      (content.groups || []).forEach((group, gIdx) => {
        (group.sentences || []).forEach((sentence, sIdx) => {
          if (sentence.languageText && sentence.languageText.trim()) {
            languageTexts.push(sentence.languageText);
            sentenceRefs.push({ type: 'language', gIdx, sIdx });
          }
          if (sentence.englishText && sentence.englishText.trim()) {
            englishTexts.push(sentence.englishText);
            sentenceRefs.push({ type: 'english', gIdx, sIdx });
          }
        });
      });

      if (languageTexts.length === 0 && englishTexts.length === 0) {
        alert('No sentences found to generate audio. Please add sentences first.');
        setGeneratingAudio(false);
        return;
      }

      const updatedGroups = [...(content.groups || [])];
      let audioGenerated = false;

      // Generate language audio
      if (languageTexts.length > 0) {
        const languageRequests = languageTexts.map(text => ({
          text,
          languageCode: LanguageCodes.KANNADA,
          ssmlGender: VoiceGenders.FEMALE
        }));

        const languageAudioUrls = await generateBatchSpeechAudio(languageRequests, db, storage);

        let idx = 0;
        (content.groups || []).forEach((group, gIdx) => {
          (group.sentences || []).forEach((sentence, sIdx) => {
            if (sentence.languageText && sentence.languageText.trim()) {
              if (idx < languageAudioUrls.length) {
                const audioPath = languageAudioUrls[idx];
                if (audioPath && !audioPath.startsWith('[TTS Error:')) {
                  updatedGroups[gIdx].sentences[sIdx].languageAudio = audioPath;
                  audioGenerated = true;
                }
              }
              idx += 1;
            }
          });
        });
      }

      // Generate English audio
      if (englishTexts.length > 0) {
        const englishRequests = englishTexts.map(text => ({
          text,
          languageCode: LanguageCodes.ENGLISH,
          ssmlGender: VoiceGenders.FEMALE
        }));

        const englishAudioUrls = await generateBatchSpeechAudio(englishRequests, db, storage);

        let idx = 0;
        (content.groups || []).forEach((group, gIdx) => {
          (group.sentences || []).forEach((sentence, sIdx) => {
            if (sentence.englishText && sentence.englishText.trim()) {
              if (idx < englishAudioUrls.length) {
                const audioPath = englishAudioUrls[idx];
                if (audioPath && !audioPath.startsWith('[TTS Error:')) {
                  updatedGroups[gIdx].sentences[sIdx].englishAudio = audioPath;
                  audioGenerated = true;
                }
              }
              idx += 1;
            }
          });
        });
      }

      setContent(prev => ({ ...prev, groups: updatedGroups }));

      if (audioGenerated) {
        alert('Audio generated successfully for all sentences!');
      } else {
        alert('Failed to generate audio. Please check the sentences and try again.');
      }
    } catch (error) {
      alert('Failed to generate audio: ' + error.message);
    } finally {
      setGeneratingAudio(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Tenses</h3>
            <p className="text-sm text-gray-600 mt-1">
              Add groups with three sentences in the target language and English, plus audio and an image for each group.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium shadow-sm text-sm disabled:bg-emerald-300 disabled:cursor-not-allowed"
              onClick={generateAllAudio}
              disabled={generatingAudio || !(content.groups && content.groups.length)}
            >
              {generatingAudio ? (
                <>
                  <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FaVolumeUp className="w-4 h-4 mr-2" />
                  Generate Audio
                </>
              )}
            </button>
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm text-sm"
              onClick={addGroup}
            >
              + Add Group
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {(content.groups || []).map((group, gIdx) => (
            <div key={gIdx} className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
                  {gIdx + 1}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    id={`tenses-img-${gIdx}`}
                    onChange={e => handleFileUpload(gIdx, e.target.files[0])}
                    disabled={uploadingFiles[`${gIdx}-image`]}
                  />
                  <label
                    htmlFor={`tenses-img-${gIdx}`}
                    className="cursor-pointer px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs hover:bg-purple-100 transition-colors"
                  >
                    🖼️ Group Image
                  </label>
                  {uploadingFiles[`${gIdx}-image`] && (
                    <span className="text-purple-600 text-[10px]">Uploading...</span>
                  )}
                  {group.image && !uploadingFiles[`${gIdx}-image`] && (
                    <>
                      <button
                        type="button"
                        className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                        onClick={() => previewImage(group.image)}
                        title="Preview image"
                      >
                        View
                      </button>
                      <button
                        type="button"
                        className="p-1 text-red-600 hover:bg-red-100 rounded"
                        onClick={() => setContent(prev => {
                          const groups = [...(prev.groups || [])];
                          groups[gIdx] = { ...(groups[gIdx] || {}), image: null };
                          return { ...prev, groups };
                        })}
                        title="Remove image"
                      >
                        ✕
                      </button>
                    </>
                  )}
                </div>
                {(content.groups || []).length > 1 && (
                  <button
                    type="button"
                    className="ml-auto p-2 text-red-600 hover:bg-red-100 rounded transition-colors flex-shrink-0"
                    onClick={() => removeGroup(gIdx)}
                    title="Remove group"
                  >
                    ✕
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {(group.sentences || []).map((sentence, sIdx) => (
                  <div key={sIdx} className="bg-white rounded-lg p-3 border border-gray-200 space-y-2">
                    <div className="text-xs font-semibold text-gray-500 mb-1">
                      Sentence {sIdx + 1}
                    </div>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Language sentence"
                      value={sentence.languageText || ''}
                      onChange={e => updateSentence(gIdx, sIdx, 'languageText', e.target.value)}
                    />
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="English translation"
                      value={sentence.englishText || ''}
                      onChange={e => updateSentence(gIdx, sIdx, 'englishText', e.target.value)}
                    />
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <select
                        className="px-2 py-1 border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-xs"
                        value={sentence.tenseEnglish || ''}
                        onChange={e => {
                          const opt = TENSE_OPTIONS.find(o => o.value === e.target.value) || null;
                          updateSentence(gIdx, sIdx, 'tenseEnglish', opt ? opt.value : '');
                          updateSentence(gIdx, sIdx, 'tenseKannada', opt ? opt.kannada : '');
                        }}
                      >
                        <option value="">Select tense</option>
                        {TENSE_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        className="px-2 py-1 border border-gray-300 rounded-lg bg-gray-100 text-xs"
                        readOnly
                        placeholder="ಕಾಲ (tense)"
                        value={sentence.tenseKannada || ''}
                      />
                    </div>
                    <div className="flex flex-wrap gap-2 items-center text-xs text-gray-600 mt-1">
                      {/* Language audio controls */}
                      <input
                        type="file"
                        accept="audio/*"
                        className="hidden"
                        id={`tenses-lang-audio-${gIdx}-${sIdx}`}
                        onChange={e => handleAudioUpload(gIdx, sIdx, 'languageAudio', e.target.files[0])}
                        disabled={uploadingFiles[`${gIdx}-${sIdx}-languageAudio`]}
                      />
                      <label
                        htmlFor={`tenses-lang-audio-${gIdx}-${sIdx}`}
                        className="cursor-pointer px-2 py-1 bg-green-50 text-green-700 rounded hover:bg-green-100"
                      >
                        🎵 Lang audio
                      </label>
                      {sentence.languageAudio && (
                        <>
                          <button
                            type="button"
                            className="flex items-center px-2 py-1 bg-green-50 text-green-700 rounded hover:bg-green-100"
                            onClick={() => playAudio(sentence.languageAudio)}
                          >
                            <FaVolumeUp className="w-3 h-3 mr-1" /> Play
                          </button>
                          <button
                            type="button"
                            className="px-2 py-1 text-red-600 hover:bg-red-100 rounded"
                            onClick={() => handleAudioDelete(gIdx, sIdx, 'languageAudio')}
                          >
                            ✕
                          </button>
                        </>
                      )}

                      {/* English audio controls */}
                      <input
                        type="file"
                        accept="audio/*"
                        className="hidden"
                        id={`tenses-eng-audio-${gIdx}-${sIdx}`}
                        onChange={e => handleAudioUpload(gIdx, sIdx, 'englishAudio', e.target.files[0])}
                        disabled={uploadingFiles[`${gIdx}-${sIdx}-englishAudio`]}
                      />
                      <label
                        htmlFor={`tenses-eng-audio-${gIdx}-${sIdx}`}
                        className="cursor-pointer px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                      >
                        🎵 Eng audio
                      </label>
                      {sentence.englishAudio && (
                        <>
                          <button
                            type="button"
                            className="flex items-center px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
                            onClick={() => playAudio(sentence.englishAudio)}
                          >
                            <FaVolumeUp className="w-3 h-3 mr-1" /> Play
                          </button>
                          <button
                            type="button"
                            className="px-2 py-1 text-red-600 hover:bg-red-100 rounded"
                            onClick={() => handleAudioDelete(gIdx, sIdx, 'englishAudio')}
                          >
                            ✕
                          </button>
                        </>
                      )}

                      {(!sentence.languageAudio && !sentence.englishAudio) && (
                        <span className="text-[10px] text-gray-400">Upload or generate audio for this sentence.</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const validateTensesContent = (content) => {
  const errors = [];

  if (!content.groups || content.groups.length === 0) {
    errors.push('At least one group is required.');
  }

  return errors;
};

export default TensesActivity;
